import { useMemo, useState, type ReactNode } from "react";
import { Check, Star } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { promoteToPrimary } from "@/lib/backlogUtils";
import { cn } from "@/lib/utils";

/** One row of the picker — a person or a team, drawn by its owner. */
export interface AssignmentCandidate {
  id: string;
  /** Sort key. The list is alphabetical by this, at every call site. */
  name: string;
  /** What the row draws: an avatar and a name, a team chip. */
  render: ReactNode;
  /** Search text, when the name alone is not what someone would type (an email). */
  searchValue?: string;
}

interface AssignmentPickerProps {
  /**
   * The ordered membership. `[0]` is the primary — that position IS the model,
   * for people (`assigned_to_id`) and for teams alike.
   */
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  candidates: AssignmentCandidate[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showSearch?: boolean;
  modal?: boolean;
  searchPlaceholder?: string;
  emptyLabel?: string;
  /**
   * The footnote under the list. Deliberately SHORT and always drawn — it used
   * to appear only once something was selected, which made the popover jump
   * wider the moment the first person was added. A control that resizes as you
   * use it is a control you have to re-aim at.
   */
  hint?: string | null;
  align?: "start" | "center" | "end";
  contentClassName?: string;
  /** The trigger. Required — every consumer has its own. */
  children: ReactNode;
}

/**
 * One picker for BOTH halves of the assignment model: assign several, mark one
 * primary.
 *
 * People and teams got separate controls with separate mental models — a
 * multi-select for assignees, a single-select "queue" plus a multi-select
 * "tags" for teams — and a reader had to learn which of the three answered
 * "who owns this". They are one question with one shape now:
 *
 * - **Clicking a row toggles membership.** The first one added becomes the
 *   primary automatically, because it is simply first in the list.
 * - **The star sets the primary.** The current primary's is always visible and
 *   filled; every other member's appears on hover as an outline. A control
 *   that is always on for every row is a column of stars nobody can read as an
 *   answer — the filled one has to be the only one at rest.
 * - **Removing the primary promotes the next.** No empty-primary state exists
 *   to design for, because `filter` preserves order and `[0]` is whoever is
 *   left at the front.
 */
const AssignmentPicker = ({
  selectedIds,
  onSelectedIdsChange,
  candidates,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  showSearch = true,
  modal = false,
  searchPlaceholder = "Search...",
  emptyLabel = "No match.",
  hint = "★ is the primary assigned",
  align = "start",
  contentClassName,
  children,
}: AssignmentPickerProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const onOpenChange = controlledOnOpenChange ?? setInternalOpen;

  // Sorted here, not at each call site, so every consumer gets a stable
  // alphabetical list without remembering to ask.
  const sorted = useMemo(() => [...candidates].sort((a, b) => a.name.localeCompare(b.name)), [candidates]);

  const primaryId = selectedIds[0];

  const toggle = (id: string) =>
    onSelectedIdsChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal={modal}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align={align} className={cn("w-72 bg-card p-0", contentClassName)}>
        <Command className="bg-card">
          {showSearch && <CommandInput placeholder={searchPlaceholder} className="text-foreground text-sm" />}
          <CommandList className="bg-card">
            <CommandEmpty className="py-2 text-sm text-muted-foreground">{emptyLabel}</CommandEmpty>
            <CommandGroup className="bg-card">
              {sorted.map((candidate) => {
                const member = selectedIds.includes(candidate.id);
                const isPrimary = primaryId === candidate.id;
                return (
                  <CommandItem
                    key={candidate.id}
                    value={candidate.searchValue ?? candidate.name}
                    // `group/row` so the promote star can key off THIS row's
                    // hover. cmdk's own `data-[selected=true]` is the keyboard
                    // highlight, which the star has to answer too — otherwise
                    // the control is mouse-only.
                    className="group/row cursor-pointer text-sm text-foreground data-[selected=true]:bg-focus/10 data-[selected=true]:text-focus"
                    onSelect={() => toggle(candidate.id)}
                  >
                    <Check className={cn("mr-2 h-3 w-3 shrink-0", member ? "opacity-100" : "opacity-0")} />
                    <span className="flex min-w-0 flex-1 items-center gap-2">{candidate.render}</span>
                    {member && (
                      <span
                        role="button"
                        tabIndex={-1}
                        aria-label={isPrimary ? "Primary" : `Make primary`}
                        title={isPrimary ? "Primary" : "Make primary"}
                        onClick={(e) => {
                          // The row's own click toggles membership. Promoting
                          // is a different verb on the same row, so it must
                          // not also un-assign the person it just promoted.
                          e.stopPropagation();
                          if (!isPrimary) onSelectedIdsChange(promoteToPrimary(selectedIds, candidate.id));
                        }}
                        className={cn(
                          "ml-2 shrink-0 rounded p-0.5 transition-all",
                          isPrimary
                            ? "text-[hsl(var(--ring))]"
                            : "text-muted-foreground/70 opacity-0 hover:scale-110 hover:text-[hsl(var(--ring))] group-hover/row:opacity-100 group-data-[selected=true]/row:opacity-100",
                        )}
                      >
                        <Star className={cn("h-3.5 w-3.5", isPrimary && "fill-current")} />
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {hint && (
            <p className="border-t border-border bg-muted/40 px-3 py-1.5 text-[10.5px] leading-snug text-muted-foreground">
              {hint}
            </p>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default AssignmentPicker;
